import React from "react";
import DataTable from "../../../globalComponents/ui/DataTable/DataTable";

const formatDate = (d) => {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return d; }
};

const DOC_SENSE_STATUS_LABEL = {
  COMPLETED: "Analyzed",
  FAILED: "Failed",
  PROCESSING: "Processing",
  PENDING: "Queued",
  TIMED_OUT: "Failed",
};

/**
 * `statusById` (docSenseId -> real DocSense status) lets callers who've
 * fetched the authoritative status show it here instead of inferring
 * "Analyzed" from docSenseId's mere presence, which is also true for
 * documents whose analysis actually failed.
 */
const statusLabel = (doc, statusById) => {
  if (doc.docSenseId) {
    const realStatus = statusById?.[doc.docSenseId];
    if (realStatus) {
      const label = DOC_SENSE_STATUS_LABEL[realStatus] || realStatus;
      return label === "Analyzed" && doc.isVerified ? "Verified" : label;
    }
    return doc.isVerified ? "Verified" : "Analyzed";
  }
  // A document never dispatched to DocSense (shouldParse:false at save time) can
  // still be isVerified — trusted immediately since there was nothing to parse it
  // against. Must be checked before falling through to "Not parsed".
  if (doc.isVerified) return "Verified";
  if (doc.shouldParse) return "Queued";
  return "Not parsed";
};

const STATUS_STYLES = {
  Verified: { bg: "#dcfce7", color: "#166534" },
  Analyzed: { bg: "#eef2ff", color: "#4338ca" },
  Failed: { bg: "#fee2e2", color: "#991b1b" },
  Processing: { bg: "#e0f2fe", color: "#0369a1" },
  Queued: { bg: "#fef9c3", color: "#854d0e" },
  "Not parsed": { bg: "#f1f5f9", color: "#64748b" },
};

const StatusPill = ({ value }) => {
  const s = STATUS_STYLES[value] || STATUS_STYLES["Not parsed"];
  return (
    <span style={{ fontSize: 10, fontWeight: 700, padding: "3px 9px", borderRadius: 20, background: s.bg, color: s.color, whiteSpace: "nowrap" }}>
      {value}
    </span>
  );
};

/** Re-analysis is currently allowed for any already-analyzed document (including
 *  COMPLETED), not just failed ones — a temporary product decision so users can
 *  force a re-run on demand. Revisit if this needs tightening to failures only. */
const isRetryable = (doc) => !!doc.docSenseId;

const EvidenceTable = ({
  documents, onView, onPreview, onReview, onDelete, onMarkVerified, canDelete, statusById,
  selectable, selectedIds, onToggleSelect, allSelected, onToggleSelectAll,
}) => {
  const rows = documents.map((doc) => ({
    _id: doc.id,
    fileName: doc.fileName,
    status: statusLabel(doc, statusById),
    uploadedBy: doc.uploadedByName || "—",
    createdAt: formatDate(doc.createdAt),
    _raw: doc,
  }));

  const columns = [
    ...(selectable ? [{
      key: "_select",
      label: "",
      width: 50,
      renderHeader: () => (
        <input
          type="checkbox"
          aria-label="Select all for reschedule"
          checked={allSelected}
          onChange={onToggleSelectAll}
          title="Select all"
        />
      ),
      renderCell: (_value, row) => {
        const retryable = isRetryable(row._raw);
        return (
          <input
            type="checkbox"
            aria-label={`Select ${row.fileName} for reschedule`}
            checked={selectedIds.has(row._id)}
            disabled={!retryable}
            onChange={() => onToggleSelect(row._id)}
            title={retryable ? "Select to reschedule analysis" : "Not yet submitted for analysis"}
          />
        );
      },
    }] : []),
    { key: "fileName", label: "File Name", sortable: true },
    { key: "status", label: "Status", renderCell: (value) => <StatusPill value={value} /> },
    { key: "uploadedBy", label: "Uploaded By" },
    { key: "createdAt", label: "Uploaded", sortable: true },
  ];

  return (
    <DataTable
      title=""
      searchable={false}
      showPagination={rows.length > 20}
      columns={columns}
      data={rows}
      emptyMessage="No evidence documents found."
      extraActions={(row) => [
        { label: "Preview", icon: "fas fa-eye", variant: "view", onClick: () => onPreview(row._raw) },
        { label: "View / Edit", icon: "fas fa-info-circle", variant: "view", onClick: () => onView(row._raw) },
        // Single entry point into DocSense analysis — replaces the old View/Edit/Review AI
        // trio that all opened the same modal. Only shown once there's something to inspect.
        ...(row._raw.docSenseId ? [{ label: "DocSense Analysis", icon: "fas fa-robot", variant: "edit", onClick: () => onReview(row._raw) }] : []),
        ...(!row._raw.isVerified ? [{
          label: "Mark Verified", icon: "fas fa-check-circle", variant: "save", iconColor: "#16a34a", onClick: () => onMarkVerified(row._raw),
        }] : []),
        ...(canDelete ? [{ label: "Delete", icon: "fas fa-trash", variant: "del", onClick: () => onDelete(row._raw) }] : []),
      ]}
    />
  );
};

export default EvidenceTable;
